import Button from "components/Button";
import Input from "components/Input";
import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import apiService from "services/apiService";
import { FiLock, FiCheckCircle, FiAlertCircle } from "react-icons/fi";
import { useToaster } from "contexts/toasterContext";
import { useNavigate } from "react-router-dom";
// import { storageService } from "services/storageService";

interface ChangePasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

interface ResetPasswordProps {
onClose: () => void;
}

const passwordStrengthSchema = yup
  .string()
  .min(8, "Password must be between 8 and 16 characters")
  .max(16, "Password must be between 8 and 16 characters")
  .matches(
    /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9\s])[^\s]+$/,
    "Password must contain at least one digit, one lowercase letter, one uppercase letter, one special character, and no whitespace"
  );

const ChangePassword = ({ onClose }: ResetPasswordProps) => {
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

 const { addToast } = useToaster();
  const navigate = useNavigate();

  const methods = useForm<ChangePasswordFormValues>({
    mode: "onChange",
  });

  const newPassword = methods.watch("newPassword", "");

  // const getPasswordStrength = (password: string) => {
  //   let score = 0;
  //   if (password.length >= 8) score++;
  //   if (/[A-Z]/.test(password)) score++;
  //   if (/[a-z]/.test(password)) score++;
  //   if (/[0-9]/.test(password)) score++;
  //   if (/[@$!%*?&#^()_+\-=]/.test(password)) score++;
  //   return score;
  // };

  // const strengthScore = getPasswordStrength(newPassword);
  // const strengthLabel = ["", "Very Weak", "Weak", "Fair", "Strong", "Very Strong"][strengthScore];
  // const strengthColor = ["", "bg-red-500", "bg-orange-400", "bg-yellow-400", "bg-blue-500", "bg-green-500"][strengthScore];

  const handleSubmit = async (data: ChangePasswordFormValues) => {
    setSuccessMsg(null);
    setErrorMsg(null);
    setLoading(true);

    const payload = {
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
      confirmPassword: data.confirmNewPassword,
    };

    try {
      await apiService.put(`/api/change-password`, payload).then(async (res) => {
        console.log("Response:", res);
        onClose();
        addToast("Password changed successfully. Please log in again.", "success");

        // --- Execute Logout Flow ---
        try {
          await apiService.post("/api/auth/logout", {});
        } catch (logoutErr) {
          console.log("Logout API failed:", logoutErr);
        } finally {
          // Clear all authentication storage
          localStorage.clear();
          sessionStorage.clear();

          // Revert theme to light mode
          window.document.documentElement.classList.remove("dark");
          localStorage.setItem("theme", "light");

          // Redirect to login
          navigate("/login");
        }
      });

      methods.reset();
    } catch (err: unknown) {
      let msg = "Failed to change password. Please check your current password.";

      if (err instanceof Error) {
        msg = err.message;
      }

      // Axios error handling
      if (typeof err === "object" && err !== null && "response" in err) {
        const error = err as {
          response?: {
            data?: {
              message?: string;
            } | string;
          };
        };

        msg =
          typeof error.response?.data === "string"
            ? error.response.data
            : error.response?.data?.message || msg;
      }

      addToast(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30">
          <FiLock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Change Password</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">Update your account password</p>
        </div>
      </div>

      {/* Success Banner */}
      {successMsg && (
        <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-700">
          <FiCheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
          <p className="text-sm text-green-700 dark:text-green-400">{successMsg}</p>
        </div>
      )}

      {/* Error Banner */}
      {errorMsg && (
        <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-700">
          <FiAlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-400">{errorMsg}</p>
        </div>
      )}

      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmit)} noValidate>
          {/* Current Password */}
          <Input
            name="currentPassword"
            label="Current Password"
            type="password"
            placeholder="Enter current password"
            required
            disableAutoCapitalize
            validationSchema={yup.string().required("Current password is required")}
          />

          {/* New Password */}
          <Input
            name="newPassword"
            label="New Password"
            type="password"
            placeholder="Enter new password"
            required
            disableAutoCapitalize
            validationSchema={passwordStrengthSchema.required("New password is required")}
          />

          {/* Password Strength Meter */}
          {/* {newPassword.length > 0 && (
            <div className="mb-3 -mt-1">
              <div className="flex gap-1 mb-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                      i <= strengthScore ? strengthColor : "bg-gray-200 dark:bg-gray-600"
                    }`}
                  />
                ))}
              </div>
              <p className={`text-xs font-medium ${
                strengthScore <= 2 ? "text-red-500" :
                strengthScore === 3 ? "text-yellow-500" :
                strengthScore === 4 ? "text-blue-500" : "text-green-500"
              }`}>
                {strengthLabel}
              </p>
            </div>
          )} */}

          {/* Confirm New Password */}
          <Input
            name="confirmNewPassword"
            label="Confirm New Password"
            type="password"
            placeholder="Re-enter new password"
            required
            disableAutoCapitalize
            validationSchema={yup
              .string()
              .required("Please confirm your new password")
              .test("passwords-match", "Passwords do not match", (value) => value === newPassword)}
          />

          {/* Password Requirements */}
          <div className="mb-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Password requirements:</p>
            <ul className="space-y-1">
              {[
                { test: newPassword.length >= 8 && newPassword.length <= 16, label: "Between 8 and 16 characters" },
                { test: /[A-Z]/.test(newPassword), label: "One uppercase letter" },
                { test: /[a-z]/.test(newPassword), label: "One lowercase letter" },
                { test: /[0-9]/.test(newPassword), label: "One number" },
                { test: /[^A-Za-z0-9\s]/.test(newPassword), label: "One special character" },
                { test: newPassword.length > 0 && !/\s/.test(newPassword), label: "No whitespace allowed" },
              ].map(({ test, label }) => (
                <li key={label} className="flex items-center gap-1.5 text-xs">
                  <span className={`w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0 ${test ? "bg-green-500" : "bg-gray-200 dark:bg-gray-600"
                    }`}>
                    {test && (
                      <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </span>
                  <span className={test ? "text-green-600 dark:text-green-400" : "text-gray-500 dark:text-gray-400"}>
                    {label}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mb-2">
            <Button type="button"  fullWidth={false} color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              color="gray"
              fullWidth={false}
              onClick={() => {
                methods.reset();
                setSuccessMsg(null);
                setErrorMsg(null);
              }}
            >
              Reset
            </Button>
            <Button type="submit" color="blue" fullWidth={false} loading={loading}>
              Update Password
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default ChangePassword;